import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Input, Image, Button, Form, Select, message, DatePicker, InputNumber } from "antd";
import { RcFile } from "antd/es/upload/interface";
import { v4 as uuidv4 } from "uuid";
import { ApolloError } from "@apollo/client";
import dayjs from "dayjs";

import {
	Installed_Products_Set_Input,
	Installed_Product_Attachments_Insert_Input,
	Products,
	Maybe
} from "graphql/generated/graphql";
import { useGetInstalledProductByIdQuery } from "graphql/queries/get-installed-product-by-id.generated";
import { useDeleteInstalledProductAttachmentByIdMutation } from "graphql/mutations/delete-installed-attachment-by-id.generated";
import { useUpdateInstalledProductByIdMutation } from "graphql/mutations/update-installed-product-by-id.generated";
import { useAddInstalledProductAttachmentsMutation } from "graphql/mutations/add-installed-product-attachment.generated";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useSelectedAttachments, clearSelectedAttachments } from "state/reactive-vars/selected-attachment-var";

import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";
import UploadPhotoModal from "components/upload-photo-modal/upload-photo-modal";
import AttachmentItem from "components/attachment-item/attachment-item";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import LocationHierarchySelect, {
	LocationHierarchyValue,
	emptyLocationHierarchyValue
} from "components/location-hierarchy-select/location-hierarchy-select";

import routeNames from "constants/route-names";
import { pruneEmptyValues } from "utils/helpers";
import { deleteFromStorage, getFileExtension, uploadToStorage } from "utils/files-management";
import { UserRoleValue } from "constants/field-values";

const { TextArea } = Input;

const InstalledProductsEdit: React.FC = () => {
	const params = useParams<{ installedDevId: string }>();
	const navigate = useNavigate();
	const { loggedUserValue } = useLoggedUser();
	const { updateSelectedAttachments } = useSelectedAttachments();
	const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");
	const [serial, setSerial] = useState<string>("");
	const [installedProductNotes, setInstalledProductNotes] = useState<string>("");
	const [installationDate, setInstallationDate] = useState<dayjs.Dayjs | null>(null);
	const [warrantyExpirationDate, setWarrantyExpirationDate] = useState<dayjs.Dayjs | null>(null);
	const [installationLocation, setInstallationLocation] = useState<string>("");
	const [locationHierarchy, setLocationHierarchy] = useState<LocationHierarchyValue>(emptyLocationHierarchyValue);

	const [uploading, setUploading] = useState(false);
	const [addAttachmentModalOpen, setAddAttachmentModalOpen] = useState<boolean>(false);
	const [addAttachmentModalEditMode, setAddAttachmentModalEditMode] = useState<boolean>(false);
	const [attachmentsFiles, setAttachmentsFiles] = useState<Array<any>>([]);
	const [existingAttachmentsFiles, setExistingAttachmentsFiles] = useState<Array<any>>([]);

	const [installedProductInputCompleted, setInstalledProductInputCompleted] = useState(false);

	// Handle product or installed product photo
	const [addProductPhotoModalOpen, setAddProductPhotoModalOpen] = useState<boolean>(false);
	const [productPhotoThumbUrl, setProductPhotoThumbUrl] = useState<string>("");
	const [installedProductPhotoThumbUrl, setInstalledProductPhotoThumbUrl] = useState<string>("");
	const [installedProductPhotoFile, setInstalledProductPhotoFile] = useState<RcFile | null>(null);

	const [selectedProduct, setSelectedProduct] = useState<Maybe<Products>>(null);

	const handleOnCompleteGetInstalledProductById = useCallback((data: any) => {
		const { installed_products_by_pk } = data;
		const {
			property_id,
			product,
			product_serial,
			installation_date,
			warranty_expiration_date,
			location,
			building_id,
			floor_id,
			room_id,
			product_notes,
			installed_product_attachments,
			photo_link
		} = installed_products_by_pk[0];

		setSelectedPropertyId(property_id || "");
		setSelectedProduct(product || "");
		setSerial(product_serial || "");
		setInstalledProductNotes(product_notes);
		setInstallationLocation(location || "");
		setLocationHierarchy({
			buildingId: building_id || null,
			floorId: floor_id || null,
			roomId: room_id || null
		});
		setInstalledProductPhotoThumbUrl(photo_link);
		setProductPhotoThumbUrl(product?.photo_link);

		if (installation_date) {
			const existingInstallationDate = dayjs(installation_date);
			setInstallationDate(existingInstallationDate);
		}

		if (warranty_expiration_date) {
			const existingWarrantyExpirationDate = dayjs(warranty_expiration_date);
			setWarrantyExpirationDate(existingWarrantyExpirationDate);
		}

		if (installed_product_attachments && installed_product_attachments.length) {
			setExistingAttachmentsFiles(installed_product_attachments);
		}
	}, []);

	const handleOnErrorGetInstalledProductById = useCallback(error => {
		console.log("useGetInstalledProductByIdQuery Error: ", error);
	}, []);

	const { loading } = useGetInstalledProductByIdQuery({
		skip: !params.installedDevId,
		variables: {
			id: params.installedDevId,
			is_not_deleted: true
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorGetInstalledProductById,
		onCompleted: handleOnCompleteGetInstalledProductById
	});

	const handleOnCloseAttachmentModal = useCallback(async () => {
		setAddAttachmentModalOpen(false);
		setAddAttachmentModalEditMode(false);
		clearSelectedAttachments();
	}, []);

	const handleOnCompleteAddInstalledProductAttachments = useCallback((data: any) => {
		setAttachmentsFiles([]);
	}, []);

	const handleOnErrorAddInstalledProductAttachments = useCallback((error: ApolloError) => {
		console.log("handleOnErrorAddInstalledProductAttachments Error: ", error);
	}, []);

	const [addInstalledProductAttachmentsMutation, { loading: loadingAddInstalledProductAttachments }] =
		useAddInstalledProductAttachmentsMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorAddInstalledProductAttachments,
			onCompleted: handleOnCompleteAddInstalledProductAttachments
		});

	const handleOnCompleteDeleteInstalledProductAttachment = useCallback(
		(data: any) => {
			const { delete_installed_product_attachments_by_pk } = data;

			const newList = existingAttachmentsFiles.filter(
				element => element.id !== delete_installed_product_attachments_by_pk.id
			);

			setExistingAttachmentsFiles(newList);
		},
		[existingAttachmentsFiles]
	);

	const handleOnErrorDeleteInstalledProductAttachment = useCallback((error: ApolloError) => {
		console.log("Delete Installed Product Attachment Error: ", error);
	}, []);

	const [deleteInstalledProductAttachmentByIdMutation, { loading: deleteInstalledProductAttachmentLoading }] =
		useDeleteInstalledProductAttachmentByIdMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorDeleteInstalledProductAttachment,
			onCompleted: handleOnCompleteDeleteInstalledProductAttachment
		});

	const handleOnRemoveExistingAttachment = useCallback(async (attachmentFile: any) => {
		const { id, photo_link, document_link, video_link, audio_link, site_link } = attachmentFile;

		try {
			const docLink = photo_link || document_link || video_link || audio_link || site_link;

			await deleteFromStorage(docLink);
		} catch (error) {
			console.error("Error deleting Installed AttachmentFile  from firebase:", error);
		}

		try {
			if (id) {
				deleteInstalledProductAttachmentByIdMutation({
					variables: {
						id: id
					}
				});
			}
		} catch (error) {
			console.error("Error deleting Installed AttachmentFile from database:", error);
		}
	}, []);

	const handleOnRemoveAttachment = useCallback(
		(attachmentFile: any) => {
			const { uid, type, description, file } = attachmentFile;

			const newList = attachmentsFiles.filter(element => element.uid !== uid);

			if (attachmentsFiles) {
				setAttachmentsFiles(newList);
			}
		},
		[attachmentsFiles]
	);

	const handleOnEditAttachment = useCallback(
		(attachmentFile: any) => {
			const { uid, type, description, file, name } = attachmentFile;

			updateSelectedAttachments(attachmentFile);

			setAddAttachmentModalEditMode(true);
			setAddAttachmentModalOpen(true);
		},
		[updateSelectedAttachments]
	);

	const updateAttachments = useCallback((attachmentFile: any) => {
		const { type, uid } = attachmentFile;

		setAttachmentsFiles(prev => {
			const filteredList = prev.filter(element => element.uid !== uid);

			return filteredList.concat([attachmentFile]);
		});
	}, []);

	const handleOnCompleteUpdateInstalledProductMutation = useCallback(
		(data: any) => {
			if (selectedPropertyId) {
				const path = `${routeNames.ADMIN_PROPERTY_DETAILS.replace(":propertyId", selectedPropertyId)}`;

				navigate(path);
			}
		},
		[selectedPropertyId]
	);

	const handleOnErrorUpdateInstalledProductMutation = useCallback((error: ApolloError) => {
		console.log("Error Update Installed Product Error: ", error);
	}, []);

	const [updateInstalledProductByIdMutation, { loading: loadingUpdateInstalledProducts }] =
		useUpdateInstalledProductByIdMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorUpdateInstalledProductMutation,
			onCompleted: handleOnCompleteUpdateInstalledProductMutation,
			refetchQueries: ["getPropertyById", "getPropertiesFiltered", "getPropertiesByWorkspaceId"]
		});

	const uploadAttachments = useCallback(async () => {
		if (attachmentsFiles.length > 0) {
			setUploading(true);

			try {
				const promises: Promise<Installed_Product_Attachments_Insert_Input | undefined>[] = [];

				attachmentsFiles.forEach((attachment, index) => {
					const { type, description, file, name } = attachment;

					const fileMediaType = file.type.split("/")[0];
					const uniqueID = uuidv4();

					let bucketName = "installed-product-attachments/";
					let attachmentName = "";
					const fileExtension = getFileExtension(file.name);
					let fileName = name ? `${name}.${fileExtension}` : file.name;

					const newAttachment: Installed_Product_Attachments_Insert_Input = {
						installed_product_id: params.installedDevId,
						photo_link: null,
						document_link: null,
						video_link: null,
						audio_link: null,
						type,
						description,
						name,
						mime_type: file.type
					};

					switch (fileMediaType) {
						case "image":
							bucketName = bucketName + "photos";
							attachmentName = `attachment-photo-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						case "video":
							bucketName = bucketName + "videos";
							attachmentName = `attachment-video-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						case "audio":
							bucketName = bucketName + "audios";
							attachmentName = `attachment-audio-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						default:
							bucketName = bucketName + "documents";
							attachmentName = `attachment-document-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
					}

					const uploadTask = async () => {
						try {
							const fileObj = file?.originFileObj;

							const { publicUrl } = await uploadToStorage(fileObj, () => {}, `${bucketName}/${attachmentName}`);

							switch (fileMediaType) {
								case "image":
									newAttachment.photo_link = publicUrl;
									break;
								case "video":
									newAttachment.video_link = publicUrl;
									break;
								case "audio":
									newAttachment.audio_link = publicUrl;
									break;
								default:
									newAttachment.document_link = publicUrl;
									break;
							}

							return newAttachment;
						} catch (error) {
							console.log("Error when trying to upload the attachment file to firestore. Error: ", error);
						}
					};

					promises.push(uploadTask());
				});

				try {
					const promisesResults = await Promise.all(promises);

					const filteredPromisesResults = pruneEmptyValues(
						promisesResults
					) as Installed_Product_Attachments_Insert_Input[];

					addInstalledProductAttachmentsMutation({
						variables: {
							objects: filteredPromisesResults
						}
					});
				} catch (error) {
					console.log("Error trying to upload attachments: ", error);
				}
			} catch (error) {
				console.error("Error uploading file:", error);
				message.error("Failed to upload file.");
			} finally {
				setUploading(false);
			}
		}

		return;
	}, [attachmentsFiles, params.installedDevId]);

	const uploadProductImg = useCallback(async () => {
		if (installedProductPhotoFile) {
			try {
				const productName = selectedProduct?.name;
				const uniqueID = uuidv4();

				const bucketName = "installed-product-photos/";

				const fileName = `installed-product-photo${
					productName ? `-${productName}` : ""
				}-${uniqueID}-${Math.floor(Date.now() / 1000)}-${installedProductPhotoFile.name}`;

				const { publicUrl } = await uploadToStorage(installedProductPhotoFile, () => {}, `${bucketName}/${fileName}`);

				setInstalledProductPhotoThumbUrl(publicUrl);
			} catch (error) {
				console.error("Error uploading file:", error);
				message.error("Failed to upload file.");
			}
		}
	}, [installedProductPhotoFile, selectedProduct]);

	const handleOnSubmit = useCallback(async () => {
		if (installedProductPhotoFile) {
			await uploadProductImg();
		}

		if (attachmentsFiles.length) {
			await uploadAttachments();
		}

		setInstalledProductInputCompleted(true);
	}, [attachmentsFiles, installedProductPhotoFile]);

	// Handle installed product photo
	const handleOnConfirmPhoto = useCallback((file: any, url: string) => {
		setInstalledProductPhotoThumbUrl(url);
		setInstalledProductPhotoFile(file);
	}, []);

	const handleOnRemovePhoto = useCallback(async () => {
		const photo_link = installedProductPhotoThumbUrl;

		// Extract and store the storage path from the URL
		if (photo_link) {
			// Firebase Storage URLs contain a token, we need to extract just the path

			try {
				await deleteFromStorage(photo_link);
			} catch (error) {
				console.error("Error deleting Installed Product Photo from firebase:", error);
			}

			try {
				// Update property in database to remove the image URL
				await updateInstalledProductByIdMutation({
					variables: {
						set: {
							photo_link: null
						},
						id: params.installedDevId
					}
				});

				// Clear local state
				setInstalledProductPhotoThumbUrl("");
				setInstalledProductPhotoFile(null);

				message.success("Device photo deleted successfully");
			} catch (error) {
				console.error("Error deleting Device photo:", error);
				message.error("Failed to delete Device photo");
			}
		}
	}, [selectedProduct, params.installedDevId, updateInstalledProductByIdMutation]);

	const uploadProduct = useCallback(async () => {
		let installedProductInput: Installed_Products_Set_Input = {
			product_serial: serial,
			location: installationLocation,
			// Send the current cascade selection; the DB trigger re-derives ancestors and validates the
			// location stays inside the device's property. Nulls clear the association.
			building_id: locationHierarchy.buildingId,
			floor_id: locationHierarchy.floorId,
			room_id: locationHierarchy.roomId,
			product_notes: installedProductNotes,
			photo_link: installedProductPhotoThumbUrl
		};

		if (installationDate) {
			installedProductInput.installation_date = installationDate.format("YYYY-MM-DDTHH:mm:ssZ");
		}

		if (warrantyExpirationDate) {
			installedProductInput.warranty_expiration_date = warrantyExpirationDate.format("YYYY-MM-DDTHH:mm:ssZ");
		}

		updateInstalledProductByIdMutation({
			variables: {
				set: installedProductInput,
				id: params.installedDevId
			}
		});
	}, [
		params.installedDevId,
		serial,
		installationDate,
		warrantyExpirationDate,
		installationLocation,
		locationHierarchy,
		installedProductNotes,
		attachmentsFiles,
		loggedUserValue,
		installedProductPhotoThumbUrl
	]);

	useEffect(() => {
		if (installedProductInputCompleted) {
			uploadProduct();
		}
	}, [installedProductInputCompleted]);

	useEffect(() => {
		const canUserEdit = [
			UserRoleValue.admin_user,
			UserRoleValue.vendor_manager,
			UserRoleValue.vendor_employee
		].includes(loggedUserValue.role as UserRoleValue);
		if (loggedUserValue && !canUserEdit) {
			const path = `${routeNames.INSTALLED_DEVICES_DETAILS.replace(":installedDevId", params.installedDevId!)}`;

			navigate(path);
		}
	}, [loggedUserValue, params.installedDevId]);

	const isLoading =
		uploading ||
		loading ||
		deleteInstalledProductAttachmentLoading ||
		loadingUpdateInstalledProducts ||
		loadingAddInstalledProductAttachments;

	return (
		<div className="add-product-screen-container">
			<span className="add-product-page-title">{"Edit Device"}</span>
			{selectedProduct ? (
				<Form className="add-product-form" autoComplete="off">
					<span className="add-product-form-title">{"Device Details"}</span>
					<Form.Item className="installed-products-edit-form-item">
						<Input
							name="name"
							placeholder="Device Name"
							className="add-product-input-pair"
							value={selectedProduct.name}
							autoComplete="off"
							allowClear
							disabled={true}
						/>
					</Form.Item>

					<div className="two-inputs-container">
						<Form.Item className="installed-products-edit-form-item-pair">
							<Input
								name="make"
								placeholder="Make"
								className="add-product-input-pair"
								value={selectedProduct.make}
								autoComplete="off"
								allowClear
								disabled={true}
							/>
						</Form.Item>

						<Form.Item className="installed-products-edit-form-item-pair">
							<Input
								name="model"
								placeholder="Model"
								className="add-product-input-pair"
								value={selectedProduct.model}
								autoComplete="off"
								allowClear
								disabled={true}
							/>
						</Form.Item>
					</div>

					<div className="two-inputs-container">
						<Form.Item className="installed-products-edit-form-item-pair">
							<Input
								name="sku"
								placeholder="SKU"
								className="add-product-input-pair"
								value={selectedProduct.sku || ""}
								autoComplete="off"
								allowClear
								disabled={true}
							/>
						</Form.Item>

						<Form.Item className="installed-products-edit-form-item-pair">
							<Select
								value={selectedProduct.product_category.label}
								className="add-product-input-select"
								placeholder="Device Type"
								allowClear
								disabled={true}
							/>
						</Form.Item>
					</div>

					<Form.Item className="installed-products-edit-form-item">
						<InputNumber
							name="retailPrice"
							placeholder="Retail Price"
							className="add-product-input-pair"
							value={selectedProduct.retail_price}
							disabled={true}
						/>
					</Form.Item>

					<Form.Item className="installed-products-edit-form-item">
						<TextArea
							name="description"
							placeholder="Description"
							style={{ height: "148px" }}
							value={selectedProduct.description}
							autoComplete="off"
							allowClear
							disabled={true}
						/>
					</Form.Item>
					<div className="two-inputs-container">
						<Form.Item
							className="installed-products-edit-form-item-pair"
							rules={[
								{
									required: true,
									message: "Please input the Serial Number of the Product."
								}
							]}
							required
							validateTrigger="onBlur"
						>
							<Input
								name="serial"
								placeholder="Serial Number"
								className="add-product-input-pair"
								value={serial}
								autoComplete="off"
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
									setSerial(event.target.value);
								}}
								allowClear
								disabled={isLoading}
							/>
						</Form.Item>

						<Form.Item
							className="installed-products-edit-form-item-pair"
							rules={[
								{
									required: true,
									message: "Please input the Installation Date of the Device."
								}
							]}
							required
							validateTrigger="onBlur"
						>
							<DatePicker
								allowClear
								className="installation-date"
								format={"MM/DD/YYYY"}
								placeholder={"Installation Date"}
								value={installationDate}
								onChange={(date, dateString) => {
									if (date) {
										setInstallationDate(date);
									} else {
										setInstallationDate(null);
									}
								}}
								disabled={isLoading}
							/>
						</Form.Item>
					</div>

					<Form.Item className="installed-products-edit-form-item">
						<DatePicker
							allowClear
							className="installation-date"
							format={"MM/DD/YYYY"}
							placeholder={"Warranty Expiration Date"}
							value={warrantyExpirationDate}
							onChange={(date, dateString) => {
								if (date) {
									setWarrantyExpirationDate(date);
								} else {
									setWarrantyExpirationDate(null);
								}
							}}
							disabled={isLoading}
						/>
					</Form.Item>

					<Form.Item className="installed-products-edit-form-item">
						<LocationHierarchySelect
							propertyId={selectedPropertyId}
							value={locationHierarchy}
							onChange={setLocationHierarchy}
							disabled={isLoading}
						/>
					</Form.Item>

					<Form.Item className="installed-products-edit-form-item">
						<TextArea
							name="location"
							placeholder="Installation Location"
							value={installationLocation}
							autoComplete="off"
							onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
								setInstallationLocation(event.target.value);
							}}
							allowClear
							disabled={isLoading}
						/>
					</Form.Item>

					<Form.Item className="installed-products-edit-form-item">
						<TextArea
							placeholder="Notes"
							style={{ height: "137px" }}
							value={installedProductNotes}
							autoComplete="off"
							onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
								setInstalledProductNotes(event.target.value);
							}}
							allowClear
							disabled={isLoading}
						/>
					</Form.Item>

					<Button className="upload-product-button" onClick={() => setAddProductPhotoModalOpen(true)}>
						Upload Device Image
					</Button>

					{installedProductPhotoThumbUrl || productPhotoThumbUrl ? (
						<div className="organization-button-container">
							<Image width={170} src={installedProductPhotoThumbUrl || productPhotoThumbUrl} />{" "}
							{installedProductPhotoThumbUrl && (
								<img
									alt={`remove-organization-photo-icon`}
									src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`}
									className="organization-photo-action-icon"
									onClick={() => {
										if (!isLoading) {
											handleOnRemovePhoto();
										}
									}}
								/>
							)}
						</div>
					) : null}

					<UploadPhotoModal
						open={addProductPhotoModalOpen}
						onCancel={() => setAddProductPhotoModalOpen(false)}
						uploadMethod={handleOnConfirmPhoto}
						disableCrop
					/>
				</Form>
			) : null}

			<div className="add-product-form">
				<span className="add-product-form-title">{"Device Resources"}</span>

				<div className="add-product-documents">
					<Button
						className="upload-product-button"
						onClick={() => setAddAttachmentModalOpen(true)}
						disabled={isLoading}
					>
						Add Resources
					</Button>
				</div>

				{existingAttachmentsFiles && existingAttachmentsFiles.length > 0 ? (
					<div className="attachments-container ">
						{existingAttachmentsFiles.map(attachmentFile => {
							const uid = attachmentFile.uid || attachmentFile.id;

							return (
								<AttachmentItem
									key={`product-attachment-item-${uid}`}
									name={attachmentFile.name}
									file={attachmentFile.file}
									type={attachmentFile.type}
									onRemove={() => {
										if (!isLoading) {
											handleOnRemoveExistingAttachment(attachmentFile);
										}
									}}
									showEditButton={false}
								/>
							);
						})}
					</div>
				) : null}
				{attachmentsFiles && attachmentsFiles.length > 0 ? (
					<div className="attachments-container ">
						{attachmentsFiles.map(attachmentFile => {
							const uid = attachmentFile.uid || attachmentFile.id;

							return (
								<AttachmentItem
									key={`product-attachment-item-${uid}`}
									name={attachmentFile.name}
									file={attachmentFile.file}
									type={attachmentFile.type}
									onRemove={() => {
										if (!isLoading) {
											handleOnRemoveAttachment(attachmentFile);
										}
									}}
									showEditButton
									onEdit={() => {
										if (!isLoading) {
											handleOnEditAttachment(attachmentFile);
										}
									}}
								/>
							);
						})}
					</div>
				) : null}
			</div>

			<div className="add-product-button-container">
				<Button className="admin-add-product-button" htmlType="submit" onClick={handleOnSubmit}>
					Save
				</Button>
			</div>

			<AddAttachmentModal
				open={addAttachmentModalOpen}
				onCancel={handleOnCloseAttachmentModal}
				updateAttachments={updateAttachments}
				editMode={addAttachmentModalEditMode}
				editProduct={false}
			/>

			<LoadingOverlay isLoading={isLoading} />
		</div>
	);
};

export default InstalledProductsEdit;
